import { convertMMToPixel } from '@/utils/convertmmPixel';

// The help grid of EDI-08 drawn from the surface itself rather than from a stored image (Katia,
// 2026-10-09: "on peut pas afficher une bête grille ?"): a square every 5 mm, a stronger line every
// centimetre, and the two centre lines in the brand's colour, as an SVG the size of the print
// surface, so it lies over the canvas pixel for pixel. Never printed: it rides on the socle's grid
// overlay, which every export strips. A stand-in until main's engine draws its millimetre grid and
// rulers from the administered zone.

type TMillimetreGridParams = {
    dpi: number;
    heightInMm: number;
    widthInMm: number;
};

const MINOR_STEP_IN_MM = 5;
const MAJOR_STEP_IN_MM = 10;
const MINOR_STROKE = { colour: '#9aa8c7', widthInMm: 0.15 };
const MAJOR_STROKE = { colour: '#7086b5', widthInMm: 0.3 };
const CENTRE_STROKE = { colour: '#2CAFA5', widthInMm: 0.45 };

function buildMillimetreGridUrl({ dpi, heightInMm, widthInMm }: TMillimetreGridParams): string {
    const lines: string[] = [];
    for (let x = MINOR_STEP_IN_MM; x < widthInMm; x += MINOR_STEP_IN_MM) {
        lines.push(_line({ stroke: _strokeAt(x), x1: x, x2: x, y1: 0, y2: heightInMm }));
    }
    for (let y = MINOR_STEP_IN_MM; y < heightInMm; y += MINOR_STEP_IN_MM) {
        lines.push(_line({ stroke: _strokeAt(y), x1: 0, x2: widthInMm, y1: y, y2: y }));
    }
    lines.push(_line({ stroke: CENTRE_STROKE, x1: widthInMm / 2, x2: widthInMm / 2, y1: 0, y2: heightInMm }));
    lines.push(_line({ stroke: CENTRE_STROKE, x1: 0, x2: widthInMm, y1: heightInMm / 2, y2: heightInMm / 2 }));
    const svg = [
        `<svg xmlns="http://www.w3.org/2000/svg" width="${convertMMToPixel(widthInMm, dpi)}" height="${convertMMToPixel(heightInMm, dpi)}"`,
        ` viewBox="0 0 ${widthInMm} ${heightInMm}" preserveAspectRatio="none">`,
        ...lines,
        '</svg>',
    ].join('');
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function _strokeAt(positionInMm: number): { colour: string; widthInMm: number } {
    return positionInMm % MAJOR_STEP_IN_MM === 0 ? MAJOR_STROKE : MINOR_STROKE;
}

function _line({
    stroke,
    x1,
    x2,
    y1,
    y2,
}: {
    stroke: { colour: string; widthInMm: number };
    x1: number;
    x2: number;
    y1: number;
    y2: number;
}): string {
    return `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${stroke.colour}" stroke-width="${stroke.widthInMm}"/>`;
}

export { buildMillimetreGridUrl };
